<script lang="ts">
  interface CommitDay {
    date: string; // ISO date string
    count: number;
  }

  interface Props {
    commitData?: CommitDay[];
  }

  let { commitData = [] }: Props = $props();

  // Generate last 365 days
  const today = new Date();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(today.getFullYear() - 1);

  // Create a map of commit data for quick lookup
  const commitMap = new Map<string, number>();
  commitData.forEach(commit => {
    commitMap.set(commit.date, commit.count);
  });

  const days = [];
  let currentDate = new Date(oneYearAgo);

  while (currentDate <= today) {
    const dateStr = currentDate.toISOString().split('T')[0];
    const count = commitMap.get(dateStr) || 0;

    days.push({
      date: new Date(currentDate),
      count,
      level: 0 // Will be calculated
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Calculate levels based on count (0-4)
  days.forEach(day => {
    if (day.count === 0) day.level = 0;
    else if (day.count <= 2) day.level = 1;
    else if (day.count <= 5) day.level = 2;
    else if (day.count <= 8) day.level = 3;
    else day.level = 4;
  });
  
  // Group by weeks for the grid
  const weeks = [];
  let currentWeek = [];
  
  // Align start date to Sunday
  const startDay = days[0].date.getDay(); 
  for (let i = 0; i < startDay; i++) {
     currentWeek.push(null); // Empty placeholder for days before start of data
  }

  days.forEach(day => {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });
  if (currentWeek.length > 0) {
      while(currentWeek.length < 7) currentWeek.push(null);
      weeks.push(currentWeek);
  }

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  function getLevelColor(level: number): string {
    // Tailwind classes would be cleaner but dynamic colors are tricky with pure classes sometimes
    // Using explicit colors for clarity, can be mapped to tailwind classes
    switch (level) {
      case 0: return 'bg-gray-100 dark:bg-gray-800';
      case 1: return 'bg-green-100 dark:bg-green-900';
      case 2: return 'bg-green-300 dark:bg-green-700';
      case 3: return 'bg-green-500 dark:bg-green-500';
      case 4: return 'bg-green-700 dark:bg-green-300';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  }
</script>

<div class="w-full overflow-x-auto">
    <div class="min-w-[700px]">
        <div class="flex text-xs text-gray-400 mb-2">
            {#each weeks as week, i}
                {#if i % 4 === 0 && week[0]?.date}
                    <div class="flex-1">{months[week[0].date.getMonth()]}</div>
                {/if}
            {/each}
        </div>
        
        <div class="flex gap-1">
             <!-- Day labels -->
            <div class="flex flex-col gap-1 text-[10px] text-gray-400 pt-2 mr-1">
                <div class="h-2.5">Mon</div>
                <div class="h-2.5"></div>
                <div class="h-2.5">Wed</div>
                <div class="h-2.5"></div>
                <div class="h-2.5">Fri</div>
            </div>

            <div class="flex gap-1">
                {#each weeks as week}
                    <div class="flex flex-col gap-1">
                        {#each week as day}
                            {#if day}
                                <div 
                                    class="w-2.5 h-2.5 rounded-sm {getLevelColor(day.level)}" 
                                    title="{day.count} contributions on {day.date.toDateString()}"
                                ></div>
                            {:else}
                                 <div class="w-2.5 h-2.5"></div>
                            {/if}
                        {/each}
                    </div>
                {/each}
            </div>
        </div>
        
        <div class="flex items-center gap-2 text-xs text-gray-400 mt-4 justify-end">
            <span>Less</span>
            <div class="flex gap-1">
                <div class="w-2.5 h-2.5 rounded-sm bg-gray-100 dark:bg-gray-800"></div>
                <div class="w-2.5 h-2.5 rounded-sm bg-green-100 dark:bg-green-900"></div>
                <div class="w-2.5 h-2.5 rounded-sm bg-green-300 dark:bg-green-700"></div>
                <div class="w-2.5 h-2.5 rounded-sm bg-green-500 dark:bg-green-500"></div>
                <div class="w-2.5 h-2.5 rounded-sm bg-green-700 dark:bg-green-300"></div>
            </div>
            <span>More</span>
        </div>
    </div>
</div>
